import React, { JSX } from "react";
import { Table, TableColumnsType, Tag } from "antd";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Text from "../../components/Text/Text";
import ClampedText from "../../components/ClampedText/ClampedText";
import { actionColorOf } from "./actionColor";
import { tierAuditLogActivityLabel } from "./tierAuditLogActivity";
import { AlignTopTableContainer, AuditChangeList, CountRow } from "./styled";
import { useLocalStorage } from "../../hook/useLocalStorage";
import { dateFormatter } from "../../utility/Formatter";
import { getTierAuditLog, TierAuditLogItem } from "../../datasource/CustomerTierProgramDatasource";
import { AuditLine, changedTierNames, describeAuditAfter, describeAuditBefore } from "./tierAuditDescribe";
import TierTablePagination from "./TierTablePagination";

function isStringifiedJson(value: string): boolean {
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null;
  } catch {
    return false;
  }
}

const AUDIT_VALUE_MAX_LINES = 3;

function AuditValue({
  raw,
  lines,
  maxLines,
  onPressMore,
}: {
  raw: string | null;
  lines: AuditLine[] | null;
  maxLines?: number;
  onPressMore?: () => void;
}): JSX.Element {
  if (lines === null) {
    return <Text fontFamily="Sarabun" fontSize={14} color="Text2">{raw || "-"}</Text>;
  }
  if (lines.length === 0) return <Text fontFamily="Sarabun" fontSize={14} color="Text2">-</Text>;
  const visibleLines = maxLines !== undefined ? lines.slice(0, maxLines) : lines;
  const hiddenCount = lines.length - visibleLines.length;
  const groups: { heading?: AuditLine; items: AuditLine[] }[] = [];
  visibleLines.forEach((line) => {
    if (line.sub && groups.length > 0) groups[groups.length - 1].items.push(line);
    else if (line.sub) groups.push({ items: [line] });
    else groups.push({ heading: line, items: [] });
  });
  return (
    <div>
      {groups.map((group, idx) => (
        <div key={idx}>
          {group.heading && (
            <Text fontFamily="Sarabun" fontSize={14} color="Text2">{group.heading.text}</Text>
          )}
          {group.items.length > 0 && (
            <AuditChangeList>
              {group.items.map((item, itemIdx) => (
                <li key={itemIdx}>
                  <Text fontFamily="Sarabun" fontSize={13} color="Text3">{item.text}</Text>
                </li>
              ))}
            </AuditChangeList>
          )}
        </div>
      ))}
      {hiddenCount > 0 && (
        <div
          role='button'
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            // The row itself navigates to the shop detail page; keep this click separate.
            e.stopPropagation();
            onPressMore?.();
          }}
        >
          <Text fontFamily="Sarabun" fontSize={13} color="primary">
            +อีก {hiddenCount} รายการ · กดดูรายละเอียด
          </Text>
        </div>
      )}
    </div>
  );
}

interface Props {
  search: string;
  zone?: string | null;
  year: string;
  action?: string | null;
  page: number;
  take: number;
  onPageChange: (page: number) => void;
  onTakeChange: (take: number) => void;
}

function AuditLogTab({ search, zone, year, action, page, take, onPageChange, onTakeChange }: Props): JSX.Element {
  const [company] = useLocalStorage("profile", null);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery(
    ["tier-audit-log", company?.company, search, zone, year, action, page, take],
    () =>
      getTierAuditLog({
        company: company?.company ?? "",
        search: search || undefined,
        customerZone: zone || undefined,
        year: year && year !== "ALL" ? Number(year) : undefined,
        activity: action ? [action] : undefined,
        page,
        take,
      }),
    { enabled: !!company?.company, refetchOnWindowFocus: false, keepPreviousData: true },
  );

  const auditLog: TierAuditLogItem[] = data?.responseData?.data || [];
  const total: number = data?.responseData?.count || 0;
  const onPressAuditDetail = (record: TierAuditLogItem) => {
    navigate(`/ShopTier/audit/${record.tierAuditLogId}`, { state: { record } });
  };

  const columns: TableColumnsType<TierAuditLogItem> = [
    {
      title: "วันที่",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v: string) => (
        <div>
          <Text fontFamily="Sarabun" fontSize={14} fontWeight={500} color="Text2">
            {v ? dateFormatter(v, true).split(" ")[0] : "-"}
          </Text>
          <div>
            <Text fontFamily="Sarabun" fontSize={12} color="Text3">
              {v ? `${dateFormatter(v, true).split(" ")[1]} น.` : "-"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "หัวข้อ",
      key: "shop",
      render: (_, r) => {
        // Program-level rows have no shop; show which benefit set (and which tiers) instead.
        let title = r.customerName || "-";
        let subtitle: string | null =
          [r.customerNo, r.customerZone , r.customerProvince ? `จ.${r.customerProvince}` : null].filter(Boolean).join(" · ") || "-";
        if (r.activity === "CHANGE_REBATE_DISPLAY_POLICY") {
          // Company-level toggle: the Thai action name alone, no second line.
          title = tierAuditLogActivityLabel(r.activity, company?.company);
          subtitle = null;
        } else if (r.activity === "CREATE_PROGRAM" || r.activity === "UPDATE_PROGRAM") {
          title = `ชุดสิทธิประโยชน์ปี ${r.programYear ?? "-"}`;
          if (r.activity === "CREATE_PROGRAM") {
            subtitle = "ทุกระดับ";
          } else {
            const tierNames = changedTierNames(r.valueAfter);
            subtitle = tierNames.length > 0 ? `ระดับ ${tierNames.join(", ")}` : "-";
          }
        }
        return (
          <div>
            <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color="Text2">{title}</Text>
            {subtitle !== null && (
              <div>
                <Text fontFamily="Sarabun" fontSize={12} color="Text3">{subtitle}</Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "activity",
      key: "activity",
      render: (_,r) => {
        const label = tierAuditLogActivityLabel(r.activity, company?.company);
        return (
          <Tag style={{ borderRadius: 16, border: "none" }} color={actionColorOf(r.activity, r.updatedBy)}>
            <Text fontFamily="Sarabun" fontSize={13} fontWeight={700} color="white">{label}</Text>
          </Tag>
        );
      },
    },
    {
      title: "ก่อน",
      dataIndex: "valueBefore",
      key: "valueBefore",
      render: (_,r) => {
        if (r.valueBefore && !isStringifiedJson(r.valueBefore)) {
          const text = r.activity === "UPDATE_NAVISION" ? 
            (r.valueBefore === "true" ? "อัปโหลดแล้ว" : "รออัปโหลด") : 
            (r.activity === "CHANGE_REBATE_DISPLAY_POLICY" ? (r.valueBefore === "true" ? "แสดง" : "ซ่อน") : r.valueBefore);
          return <AuditValue raw={text} lines={null} />;
        }
        return <AuditValue raw={r.valueBefore} lines={describeAuditBefore(r.valueAfter)} />;
      },
    },
    {
      title: "หลัง",
      dataIndex: "valueAfter",
      key: "valueAfter",
      render: (v: string | null, r) => {
        const text = r.activity === "UPDATE_NAVISION" ? 
            (r.valueAfter === "true" ? "อัปโหลดแล้ว" : "รออัปโหลด") : 
            (r.activity === "CHANGE_REBATE_DISPLAY_POLICY" ? (r.valueAfter === "true" ? "แสดง" : "ซ่อน") : r.valueAfter);
        return (
          <AuditValue
            raw={text}
            lines={describeAuditAfter(v)}
            maxLines={AUDIT_VALUE_MAX_LINES}
            onPressMore={() => onPressAuditDetail(r)}
          />
        );
      },
    },
    {
      title: "เหตุผล",
      dataIndex: "remark",
      key: "remark",
      width: 200,
      render: (v: string | null) => (
        <div style={{ maxWidth: 200, wordBreak: "break-word", whiteSpace: "normal" }}>
          {v && !isStringifiedJson(v) ? (
            <ClampedText text={v} rows={3} />
          ) : (
            <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v || "-"}</Text>
          )}
        </div>
      ),
    },
    {
      title: "ผู้ดำเนินการ",
      dataIndex: "updatedBy",
      key: "updatedBy",
      render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v === "SYSTEM" ? "ระบบ" : v || "-"}</Text>,
    },
  ];

  return (
    <div>
      <CountRow>{total} รายการ</CountRow>
      <AlignTopTableContainer>
        <Table
          rowKey="tierAuditLogId"
          dataSource={auditLog}
          columns={columns}
          loading={isLoading}
          pagination={false}
          onRow={(record) => ({
            style: record.customerCompanyId ? { cursor: "pointer" } : undefined,
            onClick: () => record.customerCompanyId && navigate(`/ShopTier/detail/${record.customerCompanyId}`),
          })}
        />
      </AlignTopTableContainer>
      <TierTablePagination
        current={page}
        pageSize={take}
        total={total}
        onChange={onPageChange}
        onPageSizeChange={onTakeChange}
      />
    </div>
  );
}

export default AuditLogTab;
